// Converts GeoJSON or KML features to Parquet (point-only files) or a GeoJSON
// FeatureCollection (files with polygon/line features) using the existing
// DuckDB-WASM instance (db = dbRef.current from useDuckDB).

// ── Parse helpers ─────────────────────────────────────────────────────────────

export function parseGeoJSON(text) {
  let geojson;
  try { geojson = JSON.parse(text); } catch { throw new Error("Invalid GeoJSON — could not parse file"); }

  const features =
    geojson.type === "FeatureCollection" ? (geojson.features || []) :
    geojson.type === "Feature"           ? [geojson] : [];

  if (!features.length) throw new Error("No features found in GeoJSON file");

  let skipped    = 0;
  let hasNonPoint = false;
  const records  = [];

  for (const f of features) {
    if (!f?.geometry) { skipped++; continue; }
    if (f.geometry.type !== "Point") { hasNonPoint = true; skipped++; continue; }
    const [lon, lat, alt] = f.geometry.coordinates || [];
    if (!isFinite(lat) || !isFinite(lon)) { skipped++; continue; }
    records.push({
      lat,
      lon,
      ...(alt != null && isFinite(alt) ? { height: alt } : {}),
      ...sanitizeProps(f.properties),
    });
  }

  // Full collection (valid geometries only) used for raw GeoJSON upload path
  const geojsonCollection = {
    type: "FeatureCollection",
    features: features.filter(f => f?.geometry),
  };

  if (!records.length && !hasNonPoint) {
    throw new Error("File contains no usable features");
  }

  return { records, skipped, hasNonPoint, geojsonCollection };
}

export function parseKML(text) {
  let doc;
  try { doc = new DOMParser().parseFromString(text, "application/xml"); } catch {
    throw new Error("Invalid KML — could not parse file");
  }
  if (doc.querySelector("parsererror")) throw new Error("Invalid KML — XML parse error");

  const placemarks = Array.from(doc.querySelectorAll("Placemark"));
  if (!placemarks.length) throw new Error("No Placemarks found in KML file");

  let skipped     = 0;
  let hasNonPoint = false;
  const records   = [];
  const geoFeatures = [];

  for (const pm of placemarks) {
    const props = kmlProps(pm);
    const point = pm.querySelector("Point");

    if (point) {
      const coordText = point.querySelector("coordinates")?.textContent?.trim();
      if (!coordText) { skipped++; continue; }
      const [lon, lat, alt] = coordText.split(",").map(s => parseFloat(s.trim()));
      if (!isFinite(lat) || !isFinite(lon)) { skipped++; continue; }
      records.push({
        lat,
        lon,
        ...(alt != null && isFinite(alt) ? { height: alt } : {}),
        ...sanitizeProps(props),
      });
      geoFeatures.push({
        type: "Feature",
        geometry: { type: "Point", coordinates: isFinite(alt) ? [lon, lat, alt] : [lon, lat] },
        properties: props,
      });
      continue;
    }

    const polygon = pm.querySelector("Polygon");
    if (polygon) {
      const outer = polygon.querySelector("outerBoundaryIs LinearRing coordinates");
      if (!outer) { skipped++; continue; }
      const outerRing = parseKMLCoords(outer.textContent);
      if (!outerRing.length) { skipped++; continue; }

      const holes = [];
      for (const inner of polygon.querySelectorAll("innerBoundaryIs LinearRing coordinates")) {
        const ring = parseKMLCoords(inner.textContent);
        if (ring.length) holes.push(ring);
      }

      hasNonPoint = true;
      geoFeatures.push({
        type: "Feature",
        geometry: { type: "Polygon", coordinates: [outerRing, ...holes] },
        properties: props,
      });
      continue;
    }

    const lineString = pm.querySelector("LineString coordinates");
    if (lineString) {
      const coords = parseKMLCoords(lineString.textContent);
      if (!coords.length) { skipped++; continue; }
      hasNonPoint = true;
      geoFeatures.push({
        type: "Feature",
        geometry: { type: "LineString", coordinates: coords },
        properties: props,
      });
      continue;
    }

    skipped++;
  }

  const geojsonCollection = { type: "FeatureCollection", features: geoFeatures };

  if (!records.length && !hasNonPoint) {
    throw new Error(
      skipped
        ? `No usable features found — ${skipped} feature${skipped > 1 ? "s" : ""} could not be parsed`
        : "KML contains no usable features"
    );
  }
  if (!records.length) {
    // Pure polygon/line file — no point records, but has non-point features
    return { records: [], skipped, hasNonPoint: true, geojsonCollection };
  }

  return { records, skipped, hasNonPoint, geojsonCollection };
}

// ── Parquet conversion (point-only files) ─────────────────────────────────────

// Write records to Parquet using the shared DuckDB instance.
// Returns a Uint8Array of the resulting .parquet file.
export async function recordsToParquet(db, records) {
  if (!records.length) throw new Error("No records to convert");

  const tmpJson    = `_cl_in_${Date.now()}.json`;
  const tmpParquet = `_cl_out_${Date.now()}.parquet`;
  const jsonStr    = JSON.stringify(records);

  const conn = await db.connect();
  const tmpTable = `_cl_tmp_${Date.now()}`;
  try {
    await db.registerFileText(tmpJson, jsonStr);
    await conn.query(`CREATE TABLE ${tmpTable} AS SELECT * FROM read_json_auto('${tmpJson}', ignore_errors=true)`);
    await conn.query(`COPY ${tmpTable} TO '${tmpParquet}' (FORMAT PARQUET, COMPRESSION SNAPPY)`);
    await conn.query(`DROP TABLE ${tmpTable}`);
    const bytes = await db.copyFileToBuffer(tmpParquet);
    if (!bytes || !bytes.byteLength) throw new Error("Parquet conversion produced an empty file");
    return bytes;
  } finally {
    await conn.close();
    try { await db.dropFile(tmpJson);    } catch {}
    try { await db.dropFile(tmpParquet); } catch {}
  }
}

// ── Internal ──────────────────────────────────────────────────────────────────

function sanitizeProps(props) {
  const out = {};
  for (const [k, v] of Object.entries(props || {})) {
    const key = (k.replace(/[^a-zA-Z0-9_]/g, "_").replace(/^(\d)/, "_$1")) || "_col";
    out[key] = v;
  }
  return out;
}

function kmlProps(pm) {
  const props = {};
  const name  = pm.querySelector(":scope > name")?.textContent?.trim();
  if (name) props.name = name;
  for (const sd of pm.querySelectorAll("SimpleData")) {
    const k = sd.getAttribute("name");
    if (k) props[k] = sd.textContent.trim();
  }
  for (const d of pm.querySelectorAll("Data")) {
    const k = d.getAttribute("name");
    const v = d.querySelector("value")?.textContent?.trim();
    if (k && v != null) props[k] = v;
  }
  return props;
}

function parseKMLCoords(text) {
  return text.trim().split(/\s+/).map(triplet => {
    const parts = triplet.split(",").map(s => parseFloat(s.trim()));
    const [lon, lat, alt] = parts;
    if (!isFinite(lon) || !isFinite(lat)) return null;
    return isFinite(alt) ? [lon, lat, alt] : [lon, lat];
  }).filter(Boolean);
}
